import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { fmtAUD, BELT_SWATCHES } from '@/lib/format';
import { ArrowRight } from 'lucide-react';

export default function Catalog() {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get('/products')
            .then((r) => setProducts(r.data))
            .finally(() => setLoading(false));
    }, []);

    return (
        <div data-testid="catalog-page">
            {/* Hero */}
            <section className="relative overflow-hidden border-hairline mb-12">
                <div className="absolute inset-0">
                    <img src="https://images.unsplash.com/photo-1564415315949-7a0c4c73aab4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" alt="" className="w-full h-full object-cover opacity-40" />
                    <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/80 to-transparent" />
                </div>
                <div className="relative z-10 px-10 py-16 md:py-24 max-w-3xl">
                    <div className="font-mono-data text-xs uppercase text-crimson tracking-widest mb-4">
                        Academy Wholesale · AUD · 6–8 weeks
                    </div>
                    <h1 className="font-display text-5xl md:text-7xl uppercase tracking-tighter leading-[0.9] mb-6">
                        Gear up<br />Your <span className="text-crimson">Academy.</span>
                    </h1>
                    <p className="font-body text-zinc-300 max-w-xl mb-8">
                        The complete Locals catalog — Gi, rashguards, rank belts, heavyweight tees and tatami mats —
                        priced for franchise members. Place a quote, pay via Stripe, get it shipped.
                    </p>
                    <div className="flex flex-wrap gap-3">
                        <Link to="/price-list" data-testid="hero-pricelist-link"
                              className="border border-zinc-700 hover:border-crimson hover:text-crimson font-display uppercase text-sm tracking-wider px-5 py-3 inline-flex items-center gap-2">
                            View Price List <ArrowRight size={16} />
                        </Link>
                        <a href="#catalog" className="bg-brand hover:bg-brand-hover text-black font-display uppercase text-sm tracking-wider px-5 py-3 inline-flex items-center gap-2">
                            Browse Catalog <ArrowRight size={16} />
                        </a>
                    </div>
                </div>
            </section>

            {/* Stats strip */}
            <section className="grid grid-cols-2 md:grid-cols-4 mb-16 border-hairline">
                {[
                    ['7', 'Product lines'],
                    ['6–8 wk', 'Lead time'],
                    ['AUD', 'Currency'],
                    ['HQ', 'Direct supply'],
                ].map(([k, v]) => (
                    <div key={v} className="p-6 border-r border-zinc-800 last:border-r-0">
                        <div className="font-display text-3xl text-white">{k}</div>
                        <div className="font-mono-data text-xs uppercase text-zinc-500 mt-1">{v}</div>
                    </div>
                ))}
            </section>

            {/* Catalog */}
            <section id="catalog">
                <div className="flex items-end justify-between mb-8">
                    <h2 className="font-display text-3xl uppercase tracking-tighter">Catalog</h2>
                    <div className="font-mono-data text-xs uppercase text-zinc-500">{products.length} lines</div>
                </div>

                {loading ? (
                    <div className="text-zinc-400 font-mono-data">Loading…</div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-800">
                        {products.map((p) => (
                            <Link
                                to={`/product/${p.slug}`}
                                key={p.id}
                                data-testid={`product-card-${p.slug}`}
                                className="group bg-ink hover:bg-surface transition-colors p-6 flex flex-col"
                            >
                                <div className="relative aspect-[4/3] mb-5 overflow-hidden border border-zinc-800">
                                    <img
                                        src={p.image_url}
                                        alt={p.name}
                                        className="w-full h-full object-cover transition-transform duration-200 ease-out group-hover:scale-105"
                                    />
                                    {p.rrp && (
                                        <div className="absolute top-3 left-3 bg-crimson text-white font-mono-data text-[10px] uppercase px-2 py-1">
                                            Save {fmtAUD(p.rrp - p.price)}
                                        </div>
                                    )}
                                </div>
                                <div className="font-mono-data text-[10px] uppercase text-zinc-500 mb-2">
                                    {p.category}
                                </div>
                                <h3 className="font-display text-xl uppercase tracking-tighter mb-2 group-hover:text-crimson transition-colors">
                                    {p.name}
                                </h3>
                                <p className="font-body text-sm text-zinc-400 mb-5 flex-1 line-clamp-2">
                                    {p.description}
                                </p>
                                <div className="flex items-end justify-between border-t border-zinc-800 pt-4">
                                    <div>
                                        <div className="font-mono-data text-[10px] uppercase text-zinc-500">Your price</div>
                                        <div className="font-mono-data text-xl text-white">{fmtAUD(p.price)}</div>
                                    </div>
                                    {p.rrp && (
                                        <div className="text-right">
                                            <div className="font-mono-data text-[10px] uppercase text-zinc-500">RRP</div>
                                            <div className="font-mono-data text-sm text-zinc-400 line-through">{fmtAUD(p.rrp)}</div>
                                        </div>
                                    )}
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </section>

            {/* Belt swatches feature */}
            <section className="mt-20 border-hairline p-8">
                <div className="font-mono-data text-xs uppercase text-crimson tracking-widest mb-2">IBJJF Spec</div>
                <h2 className="font-display text-3xl uppercase tracking-tighter mb-6">Every Belt Colour</h2>
                <div className="flex flex-wrap gap-3">
                    {BELT_SWATCHES.map((b) => (
                        <div key={b.name} className="flex items-center gap-2 px-3 py-2 border border-zinc-800 bg-surface">
                            <span className="w-6 h-6 border border-zinc-700" style={{ background: b.hex }} />
                            <span className="font-mono-data text-xs">{b.name}</span>
                        </div>
                    ))}
                </div>
            </section>
        </div>
    );
}
